import { LsonObject, createClient } from "@liveblocks/client"
import { createRoomContext } from "@liveblocks/react"
import Cookies from "js-cookie"
import { Component, ReactNode } from "react"

import { LIVEBLOCKS_ENDPOINTS } from "@endpoints/liveblocks"

// Event names for Liveblocks availability notifications
const LIVEBLOCKS_UNAVAILABLE_EVENT = "liveblocks:unavailable"
const LIVEBLOCKS_AVAILABLE_EVENT = "liveblocks:available"

// Liveblocks availability state management
let isLiveblocksAvailable = true
let isNotifying = false // Prevent infinite loops
const liveblocksListeners = new Set<(available: boolean) => void>()

export const subscribeLiveblocksAvailability = (callback: (available: boolean) => void) => {
    liveblocksListeners.add(callback)
    callback(isLiveblocksAvailable) // Call immediately with current state
    return () => liveblocksListeners.delete(callback)
}

const notifyLiveblocksUnavailable = () => {
    if (isNotifying) {
        return // Prevent recursive calls
    }
    isNotifying = true

    isLiveblocksAvailable = false
    liveblocksListeners.forEach((callback) => callback(false))
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(LIVEBLOCKS_UNAVAILABLE_EVENT))
    }
    console.warn("Liveblocks service unavailable. Collaborative features disabled.")

    isNotifying = false
}

const notifyLiveblocksAvailable = () => {
    if (isNotifying) {
        return // Prevent recursive calls
    }
    isNotifying = true

    isLiveblocksAvailable = true
    liveblocksListeners.forEach((callback) => callback(true))
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(LIVEBLOCKS_AVAILABLE_EVENT))
    }
    console.log("Liveblocks service restored. Collaborative features enabled.")

    isNotifying = false
}

// Listen to window events for manual testing (allows window.dispatchEvent to work)
if (typeof window !== "undefined") {
    window.addEventListener(LIVEBLOCKS_UNAVAILABLE_EVENT, () => {
        notifyLiveblocksUnavailable()
    })
    window.addEventListener(LIVEBLOCKS_AVAILABLE_EVENT, () => {
        notifyLiveblocksAvailable()
    })
}

const client = createClient({
    authEndpoint: async (room) => {
        try {
            const response = await fetch(LIVEBLOCKS_ENDPOINTS.AUTH, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
                body: JSON.stringify({ room }),
            })

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const data = await response.json()

            // Check if Liveblocks is unavailable
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!response.ok || data.liveblocks_available === false) {
                notifyLiveblocksUnavailable()
                throw new Error("Liveblocks service unavailable")
            }

            // Notify that Liveblocks is available
            notifyLiveblocksAvailable()

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return data
        } catch (error) {
            notifyLiveblocksUnavailable()
            throw error
        }
    },
})

// Presence represents the properties that exist on every user in the Room
// and that will automatically be kept in sync. Accessible through the
// `user.presence` property. Must be JSON-serializable.
type Presence = {
    online: boolean
    // ID of the form/entity currently being edited by this user (e.g., "job-series-abc123")
    // Used for collaborative editing lock - when set, other users see this user is editing
    editingFormId: string | null
    color?: string
}

// Optionally, Storage represents the shared document that persists in the
// Room, even after all users leave. Fields under Storage typically are
// LiveList, LiveMap, LiveObject instances, for which updates are
// automatically persisted and synced to all connected clients.
type Storage = LsonObject

// Optionally, UserMeta represents static/readonly metadata on each user, as
// provided by your own custom auth back end (if used). Useful for data that
// will not change during a session, like a user's name or avatar.
type UserMeta = {
    id?: string
    info: AvatarUser
}

// Optionally, the type of custom events broadcast and listened to in this
// room. Use a union for multiple events. Must be JSON-serializable.
type RoomEvent = null

const {
    RoomProvider,
    // Exported raw (unlike the guarded wrappers below): calling it is only legal inside a RoomProvider.
    useRoom,
    useSelf: useSelfOriginal,
    useOthers: useOthersOriginal,
    useMyPresence: useMyPresenceOriginal,
    useUpdateMyPresence: useUpdateMyPresenceOriginal,
} = createRoomContext<Presence, Storage, UserMeta, RoomEvent>(client)

// Safe wrappers that don't crash when RoomProvider is not available
const useSelf = () => {
    try {
        return useSelfOriginal()
    } catch (error) {
        // RoomProvider is not available, return null
        return null
    }
}

const useOthers = () => {
    try {
        return useOthersOriginal()
    } catch (error) {
        // RoomProvider is not available, return empty array
        return []
    }
}

const useMyPresence = () => {
    try {
        return useMyPresenceOriginal()
    } catch (error) {
        // RoomProvider is not available, return default presence and no-op updater
        const defaultPresence: Presence = { online: false, editingFormId: null }
        const noOpUpdater = () => {}
        return [defaultPresence, noOpUpdater] as const
    }
}

const useUpdateMyPresence = () => {
    try {
        return useUpdateMyPresenceOriginal()
    } catch (error) {
        // RoomProvider is not available, return a no-op updater
        return () => {}
    }
}

// Error Boundary for Liveblocks - integrated here to keep all Liveblocks logic together
interface LiveblocksErrorBoundaryProps {
    children: ReactNode
    fallback: ReactNode
}

interface LiveblocksErrorBoundaryState {
    hasError: boolean
}

class LiveblocksErrorBoundary extends Component<LiveblocksErrorBoundaryProps, LiveblocksErrorBoundaryState> {
    constructor(props: LiveblocksErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(): LiveblocksErrorBoundaryState {
        return { hasError: true }
    }

    componentDidCatch(error: Error, errorInfo: unknown) {
        console.error("Liveblocks Error Boundary caught an error:", error, errorInfo)
        notifyLiveblocksUnavailable()
    }

    componentDidUpdate(prevProps: LiveblocksErrorBoundaryProps) {
        // Reset error state when props change (e.g., when trying to reconnect)
        if (prevProps.children !== this.props.children && this.state.hasError) {
            this.setState({ hasError: false })
        }
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback
        }

        return this.props.children
    }
}

export { LiveblocksErrorBoundary, RoomProvider, useMyPresence, useOthers, useRoom, useSelf, useUpdateMyPresence }
